import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { activitySchema, activityDefaults } from "../../../../models/activity";
import ObjectiveGrid from "./ObjectiveGrid";
import PropTypes from "prop-types";
import { FormPhotoUpload, OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";

/**
 * ActivityEditor
 * Editor completo: Dados básicos + Malha Pedagógica (Objetivos e Tópicos)
 */
const ActivityEditor = ({ activity, onSave, onDelete, isSaving, onChange }) => {
    const [localObjectives, setLocalObjectives] = useState([]);

    const formik = useFormik({
        initialValues: activityDefaults,
        validationSchema: activitySchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave({ ...values, objectives: localObjectives });
        },
    });

    // Sincroniza estado quando a atividade selecionada muda
    useEffect(() => {
        if (activity) {
            formik.resetForm({
                values: {
                    id: activity.id || "",
                    name: activity.name || "",
                    description: activity.description || "",
                    color: activity.color || "#3b82f6",
                    photoUrl: activity.photoUrl || null,
                    order: activity.order || 0,
                    xp: activity.xp || 0,
                    status: activity.status || GLOBAL_STATUS.ACTIVE,
                    evaluationSystem: !!activity.evaluationSystem,
                    createdAt: activity.createdAt || null,
                    createdBy: activity.createdBy || null
                }
            });
            setLocalObjectives(activity.objectives || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Recalcula o total de XP sempre que os objetivos mudam
    useEffect(() => {
        let totalXp = 0;
        localObjectives.forEach(obj => {
            (obj.topics || []).forEach(topic => {
                totalXp += (Number(topic.xp) || 0);
            });
        });
        if (formik.values.xp !== totalXp) {
            formik.setFieldValue("xp", totalXp);
        }
    }, [localObjectives, formik.values.xp, formik]);

    if (!activity) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-run-fast"></i>
                        </div>
                    </div>
                    <h5>Selecione uma atividade</h5>
                    <p className="text-muted">Escolha uma atividade na lista ao lado ou crie uma nova para gerenciar.</p>
                </div>
            </div>
        );
    }

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? formik.values.name : "Nova Atividade"}</span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Dados básicos e Malha Pedagógica"
            headerIcon="mdi-run-fast"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {formik.values.id && (
                        <NeoButton
                            variant="soft-danger"
                            square={true}
                            onClick={() => onDelete(formik.values.id)}
                            disabled={isSaving}
                            title="Excluir Atividade"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={formik.handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando..." />
            <Row className="mb-5 border-bottom pb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                        Dados Principais
                    </h6>
                </Col>

                <Col md="2" className="text-center mb-4 mb-md-0">
                    <FormPhotoUpload
                        photoPreview={formik.values.photoUrl}
                        onChange={(url) => {
                            formik.setFieldValue("photoUrl", url);
                            onChange(true);
                        }}
                        size="lg"
                    />
                </Col>

                <Col md="10">
                    <Row>
                        <Col md="6">
                            <NeoInput
                                label="Nome da Atividade"
                                type="text"
                                placeholder="Ex: Natação, Crossfit..."
                                flat={true}
                                error={formik.touched.name && formik.errors.name ? formik.errors.name : ""}
                                {...formik.getFieldProps("name")}
                            />
                        </Col>
                        <Col md="2">
                            <NeoInput
                                label="Cor"
                                type="color"
                                size="lg"
                                flat={true}
                                inputStyle={{ padding: "4px", cursor: "pointer" }}
                                {...formik.getFieldProps("color")}
                            />
                        </Col>
                        <Col md="2">
                            <NeoInput
                                label="Total XP"
                                type="number"
                                disabled={true}
                                readOnly={true}
                                flat={true}
                                inputStyle={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "var(--color-brand-success, #2e7d32)" }}
                                {...formik.getFieldProps("xp")}
                            />
                        </Col>
                        <Col md="2">
                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                <label className="neo-input-label">Status</label>
                                <div className="form-check form-switch pt-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                        onChange={(e) => {
                                            formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                            onChange(true);
                                        }}
                                        className="form-check-input custom-switch-primary cursor-pointer"
                                        id="activity-status-switch"
                                    />
                                    <label className="form-check-label ms-2 small text-muted cursor-pointer" htmlFor="activity-status-switch">
                                        {GLOBAL_STATUS_LABELS[formik.values.status] || formik.values.status}
                                    </label>
                                </div>
                            </div>
                        </Col>
                        <Col md="12">
                            <NeoInput
                                label="Descrição / Resumo da Atividade"
                                type="textarea"
                                rows="3"
                                placeholder="Informe um resumo do que esta atividade irá focar, baseado em seus objetivos e tópicos..."
                                flat={true}
                                error={formik.touched.description && formik.errors.description ? formik.errors.description : ""}
                                {...formik.getFieldProps("description")}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* SISTEMA DE AVALIAÇÃO */}
            <div className="bg-light-subtle border rounded p-3 mb-4 d-flex align-items-center justify-content-between">
                <div>
                    <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                        <i className="mdi mdi-checkbox-marked-circle-outline text-primary font-size-18"></i>
                        Sistema de Avaliação
                    </h6>
                    <p className="text-muted small mb-0 ms-4 text-secondary">
                        Selecione essa chave se essa atividade faz parte do sistema de avaliação.
                    </p>
                </div>
                <div className="form-check form-switch form-switch-md pt-1">
                    <input
                        type="checkbox"
                        role="switch"
                        name="evaluationSystem"
                        id="activity-evaluation-switch"
                        checked={!!formik.values.evaluationSystem}
                        onChange={(e) => {
                            formik.setFieldValue("evaluationSystem", e.target.checked);
                            onChange(true);
                        }}
                        className="form-check-input custom-switch-primary cursor-pointer"
                    />
                    <label className="form-check-label ms-2 text-muted fw-semibold mb-0 cursor-pointer" htmlFor="activity-evaluation-switch">
                        {formik.values.evaluationSystem ? 'Sim' : 'Não'}
                    </label>
                </div>
            </div>

            {/* MALHA PEDAGÓGICA */}
            <div className="mt-2">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-0 d-flex align-items-center">
                        <i className="mdi mdi-format-list-bulleted-type me-2 font-size-16 text-primary"></i>
                        Malha Pedagógica Integrada
                    </h6>
                </div>

                <ObjectiveGrid
                    objectives={localObjectives}
                    onChange={(newObjectives) => {
                        setLocalObjectives(newObjectives);
                        onChange(true);
                    }}
                />
            </div>
        </NeoCard>
    );
};

ActivityEditor.propTypes = {
    activity: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default ActivityEditor;
